import { useQuery } from '@tanstack/react-query';
import { Navigate } from 'react-router-dom';

import { OnboardingForm } from './onboarding-form';

import { api } from '@/lib/api';
import { useAuth } from '@/hooks/use-auth';

function defaultOrgName(user: { email: string; name?: string | null }): string {
  const n = user.name?.trim();
  if (n) {
    const first = n.split(/\s+/)[0] ?? 'My';
    return `${first}'s workspace`.slice(0, 120);
  }
  const local = user.email.split('@')[0] ?? 'my';
  const safe = local.replace(/[^a-zA-Z0-9\-_.]/g, '') || 'my';
  return `${safe}'s workspace`.slice(0, 120);
}

export default function OnboardingPage(): import('react/jsx-runtime').JSX.Element {
  const { user, isLoading: profileLoading } = useAuth();

  const { data: orgsData, isLoading: orgsLoading } = useQuery({
    queryKey: ['organizations'],
    queryFn: () => api.listOrganizations(),
    enabled: user != null,
    staleTime: 60 * 1000,
  });

  if (profileLoading || orgsLoading) {
    return (
      <div
        className="flex min-h-screen items-center justify-center"
        data-testid="onboarding-loading"
      >
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-muted border-t-foreground" />
      </div>
    );
  }

  if (user == null) {
    return <Navigate to="/login" replace />;
  }

  if ((orgsData?.organizations ?? []).length > 0) {
    return <Navigate to="/" replace />;
  }

  return (
    <OnboardingForm
      defaultOrgName={defaultOrgName({ email: user.email, name: user.name })}
      defaultProjectName="my-app"
    />
  );
}
